import * as posModels from "../../lib/api/posModels";
import cn from "classnames";
import describeNetworkError from "../../lib/describeNetworkError";
import Drawer from "../Drawer";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import OutlineButton from "../OutlineButton";
import React, {useRef, useState} from "react";
import TextInput from "../TextInput";
import StyledKeyboard from "../StyledKeyboard";
import styles from "./SendReceiptDrawer.module.css";
import ToggleButton from "../ToggleButton";
import {sendReceipt} from "../../lib/api/orders";
import {StyledKeyboardRefHandler} from "../StyledKeyboard/StyledKeyboard";

const strings = {
    sendReceiptHeader: "Send Receipt",
    sendTo: "Send to:",
    email: "Email",
    phone: "Phone",
    emailAddress: "Email Address",
    phoneNumber: "Phone Number",
    emailExample: "example@mail.com",
    phoneExample: "1234567890",
    sendReceipt: "Send Receipt",
    emailValidationError: "Entered email must be formatted correctly.",
    phoneValidationError: "Entered phone must have 10 numerical digits. ",
};

type Props = {
    order: posModels.OrderParsed;
    onClose: () => void;
};

function isEmailValid(email: string) {
    return /^[^@]+@[^@]+\.[^@]+$/.test(email.trim());
}

function isPhoneValid(phone: string) {
    return /^\d{10}$/.test(phone.trim());
}

export default function SendReceiptDrawer(props: Props) {
    const [loading, setLoading] = useState(false);
    const [emailSelected, setEmailSelected] = useState(true);
    const [input, setInput] = useState("");
    const [validationError, setValidationError] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const keyboardRef = useRef<StyledKeyboardRefHandler>(null);

    function onSubmit() {
        if (loading || !input) {
            return;
        }

        if (emailSelected && !isEmailValid(input)) {
            setValidationError(strings.emailValidationError);
            return;
        } else if (!emailSelected && !isPhoneValid(input)) {
            setValidationError(strings.phoneValidationError);
            return;
        }

        setLoading(true);
        const receiptType = emailSelected ? "email" : "phone";
        const email = emailSelected ? input : null;
        const phone = !emailSelected ? input : null;
        sendReceipt(props.order.id, receiptType, email, phone)
            .then(() => props.onClose())
            .catch((err) => {
                const description = describeNetworkError(err).join("\n");
                logger.error("Send Receipt - failed to send order.", {
                    orderId: props.order.id,
                    errorMessage: description,
                });
                setErrorMessage(description);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <>
            <Drawer
                onClose={() => {
                    if (!loading) {
                        props.onClose();
                    }
                }}
                size="large">
                <div className={styles.sendReceiptContainer}>
                    <div className={styles.sendReceiptHeader}>
                        <div className={styles.sendReceiptTitle}>
                            {strings.sendReceiptHeader}
                        </div>
                        <div className={styles.toggleContainer}>
                            <div className={styles.sendToHeader}>
                                {strings.sendTo}
                            </div>
                            <div className={styles.toggle}>
                                <ToggleButton
                                    label={strings.email}
                                    className={styles.toggleButton}
                                    checked={emailSelected}
                                    onChange={() => setEmailSelected(true)}
                                />
                                <ToggleButton
                                    label={strings.phone}
                                    className={styles.toggleButton}
                                    checked={!emailSelected}
                                    onChange={() => setEmailSelected(false)}
                                />
                            </div>
                        </div>
                        <div className={styles.inputContainer}>
                            <div className={styles.inputHeader}>
                                {emailSelected
                                    ? strings.emailAddress
                                    : strings.phoneNumber}
                            </div>
                            <TextInput
                                className={styles.textInput}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={
                                    emailSelected
                                        ? strings.emailExample
                                        : strings.phoneExample
                                }
                                ref={inputRef}
                            />
                            <div className={styles.validationError}>
                                {validationError}
                            </div>
                        </div>
                        <OutlineButton
                            label={strings.sendReceipt}
                            onClick={onSubmit}
                            disabled={!input || loading}
                        />
                    </div>
                    <div className={cn(styles.keyboardContainer)}>
                        <StyledKeyboard
                            ref={keyboardRef}
                            currentInput={input}
                            alwaysVisible={true}
                            onChange={(input) => setInput(input)}
                            onPressEnter={onSubmit}
                            inputRefs={[inputRef]}
                            layout={emailSelected ? "extended" : "numeric"}
                        />
                    </div>
                </div>
            </Drawer>
            {errorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage}
                    onClose={() => setErrorMessage("")}
                    showITInfo
                />
            ) : null}
        </>
    );
}
